import React, { useEffect, useMemo } from "react";
import TargetCard from "./TargetCard";
import FloorCard from "./FloorCard";
import EnergyUseIntensity from "./EnergyUseIntensity";
import {
  LineChart,
  mockData as mockDataLine,
} from "../../../components/charts/line";
import { BarCompareChart } from "../../../components/charts/BarCompareChart";
import {
  BarChart,
  mockDataEnergy as mockDataBar,
} from "../../../components/charts/bars";
import { CardTip } from "./Card";
import { P } from "../../../components/Common/Typo/typo";
import useSWR from "swr";
import dayjs from "dayjs";
import { TbSolarPanel2 } from "react-icons/tb";
import "dayjs/locale/th";
import { formatUnixInAppTz } from "../../../utils/datetime";

const CHART_HEIGHT = 350
const CHART_TOP_PADDING = 40
const CHART_BOTTOM_PADDING = 70
const CHART_CONTENT_HEIGHT = CHART_HEIGHT - CHART_TOP_PADDING - CHART_BOTTOM_PADDING
const CHART_LINE_GAP = 6

const barColor: Record<string, string> = {
  other: "#6DB64F",
  lighting: "#F5A623",
  hvac: "#00A4C3",
};

const energyConsumptionRename = {
  other: "ระบบไฟฟ้าอื่นๆ",
  lighting: "ระบบแสงสว่าง",
  hvac: "ระบบปรับอากาศ",
};

const EnergyConsumption = () => {
  const queryParams = new URLSearchParams(location.search);
  const month = queryParams.get("month");
  const date = month
    ? dayjs(`${month.split("-")[1]}-${month.split("-")[0]}-01`).locale('th')
    : dayjs().locale('th');
  const [energySaveCardData, setEnergySaveCardData] = React.useState<any>([]);
  const [quarterEnergyUsageSaveCardData, setQuarterEnergyUsageSaveCardData] =
    React.useState<any>([]);
  const [floorCardData, setFloorCardData] = React.useState<any>([]);
  const [eui, setEui] = React.useState<any>([]);
  const [iaqCardData, setIaqCardData] = React.useState([]);
  const [meterInterval, setMeterInterval] = React.useState<any>([]);
  const [econTarget, setEconTarget] = React.useState<any>([]);
  const [targetCardData, setTargetCardData] = React.useState<any>([]);

  const econTargetPosition = useMemo(() => {
    if (energySaveCardData.length === 0) {
      return 0;
    }
    let max =  Math.max(
      ...energySaveCardData[0]['values'].map((val: any) => val.y),
      ...energySaveCardData[1]['values'].map((val: any) => val.y)
    );

    max = Math.ceil(max / 30000) * 30000;
    return CHART_TOP_PADDING + Math.round((1 - econTarget / max) * (CHART_CONTENT_HEIGHT)) - CHART_LINE_GAP
  }, [energySaveCardData, econTarget]);

  const { data: targetData } = useSWR([
    "v1/thaihealth/target/",
    { page: "energy", date: date.format("YYYY-MM-DD") },
  ]);
  const { data: euiData } = useSWR([
    "v1/thaihealth/dashboard_data/eui/",
    { year: date.format("YYYY") },
  ]);
  const { data: energySaveData } = useSWR([
    "v1/thaihealth/graph_data/energy_saved_per_year/",
    { month: date.format("MM-YYYY") },
  ]);
  const { data: quarterEnergyUsageSaveData } = useSWR([
    "v1/thaihealth/graph_data/quarterly_energy_usage_comparison/",
    { month: date.format("MM-YYYY") },
  ]);
  const { data: floorData } = useSWR([
    "v1/thaihealth/floor_data/",
    { month: date.format("MM-YYYY"), data_type: "energy" },
  ]);

  const { data: iaqData } = useSWR(["/v1/thaihealth/air_quality/dashboard/"]);
  const { data: solarData } = useSWR(["/v1/thaihealth/solar_data/", { month: date.format("MM-YYYY") }]);

  useEffect(() => {
    const data: any = [
      {
        title: "พลังงานรวมทั้งอาคาร",
        value: targetData?.filter((data: any) => data.order === 1)[0].details
          .current_value,
        unit: targetData?.filter((data: any) => data.order === 1)[0].details
          .unit,
        percent: targetData?.filter((data: any) => data.order === 1)[0].percent,
        icon: "building",
        bgColor: "#6DAF5266",
        borderColor: "#6DB64F",
        date_type: targetData?.filter((data: any) => data.order === 1)[0]
          .details.date_type,
      },
      {
        title: "พลังงานประเภทระบบปรับอากาศ",
        value: targetData?.filter((data: any) => data.order === 2)[0].details
          .current_value,
        unit: targetData?.filter((data: any) => data.order === 2)[0].details
          .unit,
        percent: targetData?.filter((data: any) => data.order === 2)[0].percent,
        icon: "air_condition",
        bgColor: "#F5D9AA",
        borderColor: "#F5A623",
        date_type: targetData?.filter((data: any) => data.order === 1)[0]
          .details.date_type,
      },
      {
        title: "พลังงานประเภทระบบแสงสว่าง",
        value: targetData?.filter((data: any) => data.order === 3)[0].details
          .current_value,
        unit: targetData?.filter((data: any) => data.order === 3)[0].details
          .unit,
        percent: targetData?.filter((data: any) => data.order === 3)[0].percent,
        icon: "light",
        bgColor: "#9DD9E3",
        borderColor: "#00A4C3",
        date_type: targetData?.filter((data: any) => data.order === 1)[0]
          .details.date_type,
      },
    ];
    setTargetCardData(data);
  }, [targetData]);

  useEffect(() => {
    const convertRecycleDataToMockData = (data: Record<string, number>) => {
      const formattedData = Object.entries(data).map(([name, yValue]) => {
        return {
          name: name,
          values: Object.entries(yValue).map(([x, y]) => {
            const timestamp = formatUnixInAppTz(Number(x), "MMM").toLowerCase();
            return { x: timestamp, y };
          }),
        };
      });
      return formattedData;
    };

    if (energySaveData) {
      const formattedRecycleData: any =
        convertRecycleDataToMockData(energySaveData);
      setEnergySaveCardData(formattedRecycleData);
    }
    setEconTarget(energySaveData?.econ);
  }, [energySaveData]);

  useEffect(() => {
    setQuarterEnergyUsageSaveCardData(
      quarterEnergyUsageSaveData ? quarterEnergyUsageSaveData : []
    );
  }, [quarterEnergyUsageSaveData]);

  useEffect(() => {
    setEui(euiData ? euiData.eui : 0);
  }, [euiData]);

  useEffect(() => {
    const data: any = [
      {
        title: "Roof",
        unit: "kWh",
        value: floorData?.filter((data: any) => data.name === "Roof")[0].value,
        top: 10,
        left: 80,
      },
      {
        title: "ชั้น 6",
        unit: "kWh",
        value: floorData?.filter((data: any) => data.name === "6")[0].value,
        top: 20,
        left: 5,
      },
      {
        title: "ชั้น 5",
        unit: "kWh",
        value: floorData?.filter((data: any) => data.name === "5")[0].value,
        top: 30,
        left: 80,
      },
      {
        title: "ชั้น 4",
        unit: "kWh",
        value: floorData?.filter((data: any) => data.name === "4")[0].value,
        top: 40,
        left: 5,
      },
      {
        title: "ชั้น 3",
        unit: "kWh",
        value: floorData?.filter((data: any) => data.name === "3")[0].value,
        top: 48,
        left: 80,
      },
      {
        title: "ชั้น 2",
        unit: "kWh",
        value: floorData?.filter((data: any) => data.name === "2")[0].value,
        top: 60,
        left: 5,
      },
      {
        title: "ชั้น 1",
        unit: "kWh",
        value: floorData?.filter((data: any) => data.name === "1")[0].value,
        top: 68,
        left: 80,
      },
      {
        title: "ชั้น B2",
        unit: "kWh",
        value: floorData?.filter((data: any) => data.name === "B2")[0].value,
        top: 80,
        left: 5,
      },
      {
        title: "ชั้น B1",
        unit: "kWh",
        value: floorData?.filter((data: any) => data.name === "B1")[0].value,
        top: 85,
        left: 80,
      },
    ];
    setFloorCardData(data);
  }, [floorData]);

  useEffect(() => {
    const data: any = [
      {
        title: "PM 2.5",
        value: iaqData?.pm25,
        unit: "µg/m³",
        status: iaqData?.pm25_status,
        key: "pm25",
      },
      {
        title: "CO₂",
        value: iaqData?.co2,
        unit: "ppm",
        status: iaqData?.co2_status,
        key: "co2",
      },
      {
        title: "PM 10",
        value: iaqData?.pm10,
        unit: "µg/m³",
        status: iaqData?.pm10_status,
        key: "pm10",
      },
    ];
    setIaqCardData(data);
    setMeterInterval(iaqData?.ranges);
  }, [iaqData]);

  let thaiDate;
  dayjs.locale("th");
  thaiDate = date.format("MMMM");
  dayjs.locale("en");

  return (
    <div
      className="h-full w-full relative"
      style={{
        background:
          // "radial-gradient(326.84% 138.02% at 0% 0%, #F9F9F9 0%, #ECFAFF 33%, #F4FFF0 66.5%, #FEF7EE 100%)",
          "#DCFAFF",
      }}
    >
      <div className="absolute top-0 left-0 px-8 py-4 w-full h-full">
        <div className="grid grid-cols-12 gap-2 h-full">
          <div className="col-span-3 h-full">
            <TargetCard
              targetData={targetData}
              type="energy"
              targetCardData={targetCardData}
              iaqCardData={iaqCardData}
              meterInterval={meterInterval}
            />
          </div>
          <div className="col-span-6">
            <div className="flex flex-col gap-2 h-full">
              <div className="h-[60%]">
                <FloorCard
                  card_data={floorCardData}
                  title="การใช้พลังงานแยกรายชั้น (Energy Consumption)"
                  date={date}
                />
              </div>
              <div className="h-[40%] border border-[#EDEFF9] bg-white rounded-md p-[10px]">
                <div className="flex items-center justify-between">
                  <P fontSize="30px" fontWeight="700" color="#1D1D1C">
                    การเปรียบเทียบการใช้พลังงานรายไตรมาส
                  </P>
                  <div className="px-[8px] rounded-md border border-[#DADADA] bg-[#EFEFEF]">
                    <P fontSize="20px" fontWeight="700" color="#6F6F6E">
                      Unit: กิโลวัตต์ชั่วโมง, kWh
                    </P>
                  </div>
                </div>
                <BarChart
                  data={Object.keys(quarterEnergyUsageSaveCardData).reduce(
                    (acc, key) => {
                      const typedKey: any =
                        key as keyof typeof quarterEnergyUsageSaveCardData;
                      return {
                        ...acc,
                        [energyConsumptionRename[
                          typedKey as keyof typeof energyConsumptionRename
                        ] || typedKey]: {
                          ...quarterEnergyUsageSaveCardData[typedKey],
                          color: barColor[typedKey],
                        },
                      };
                    },
                    {}
                  )}
                  title=""
                  yAxisName=""
                  height="90%"
                />
              </div>
            </div>
          </div>
          <div className="col-span-3">
            <div className="flex flex-col gap-2 h-full">
              <div className="h-fit">
                <EnergyUseIntensity value={eui} date={date} />
              </div>
              <div className="h-full border border-[#EDEFF9] bg-white rounded-xl p-[10px]">
                <div className="flex items-center justify-between">
                  <P fontSize="24px" fontWeight="700" color="#1D1D1C">
                    การใช้พลังงานต่อปี
                  </P>
                  <div className="px-[8px] rounded-md border border-[#DADADA] bg-[#EFEFEF]">
                    <P fontSize="20px" fontWeight="700" color="#6F6F6E">
                      Unit: กิโลวัตต์ชั่วโมง, kWh
                    </P>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2 border border-[#E7A02B] rounded-[10px] p-2 text-[18px] bg-[#F5D9AA]">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center p-1 bg-[#000] bg-opacity-30">
                      <TbSolarPanel2 className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-[#000]">
                      การใช้พลังงานแสงอาทิตย์ประจำเดือน{thaiDate}
                    </p>
                  </div>

                  <div className="flex items-center gap-1">
                    <p className="font-bold text-[20px] text-[#000]">
                      {solarData?.solar_value?.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }) ?? 0}
                    </p>
                    <p className="text-[#000]">kwh</p>
                  </div>
                </div>
                <div className="relative">
                  <BarCompareChart
                    data={energySaveCardData}
                    title=""
                    yAxisName=""
                    height={`${CHART_HEIGHT}px`}
                    target={econTarget}
                  />
                  <p className="absolute right-0 text-[16px]" style={{ top: `${econTargetPosition}px`}}>
                    {econTarget?.toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }) ?? 0}{" "}
                    kWh
                  </p>
                </div>

                <div className="flex items-center justify-center gap-2 -mt-10">
                  <div className="flex items-center">
                    <div className="w-2.5 h-2.5 bg-[#00A4C3] mr-1"></div>
                    <P className="text-base font-bold text-gray-700">
                      {date
                        .subtract(1, "year")
                        .add(543, "year")
                        .format("YYYY")}
                    </P>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2.5 h-2.5 bg-[#6DB64F] mr-1"></div>
                    <P className="text-base font-bold text-gray-700">
                      {date.add(543, "year").format("YYYY")}
                    </P>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2.5 h-2.5 bg-amber-500 mr-1"></div>
                    <P className="text-base font-bold text-gray-700">
                      เป้าหมาย
                    </P>
                  </div>
                </div>
                <CardTip
                  title="พลังงานแสงอาทิตย์ (Solar Cell) : ผลิตจากแผงโซลาร์เซลล์ที่เปลี่ยนแสงอาทิตย์เป็นไฟฟ้า ซึ่งเป็นพลังงานสะอาดและหมุนเวียน"
                  value="พลังงานจากโครงข่ายไฟฟ้า (กริด): มาจากระบบจ่ายไฟฟ้าแบบดั้งเดิม ซึ่งรวมไฟฟ้าจากถ่านหิน ก๊าซธรรมชาติ นิวเคลียร์ ระบบกริดช่วยให้มีไฟฟ้าใช้ตลอดเวลา แต่ยังพึ่งพาเชื้อเพลิงฟอสซิลมาก"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnergyConsumption;
